import { useState, useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { CssBaseline, ThemeProvider, Box } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { PermissionAlertProvider } from "./contexts/PermissionAlertContext";
import { setLanguageFromStoredNationality } from "./utils/languageFromNationality";
import Dashboard from "./scenes/dashboard";
import Team from "./scenes/team";
import TeamStatistics from "./scenes/team/TeamStatistics";
import Bar from "./scenes/bar";
import Li̇ne from "./scenes/line";
import Faq from "./scenes/faq";
import Calendar from "./scenes/calendar";
import Patients from "./scenes/patients";
import Files from "./scenes/files";
import FilesStatistics from "./scenes/files/FilesStatistics";
import AddPatient from "./scenes/addpatient";
import Marketing from "./scenes/marketing";
import About from "./scenes/about";
import Contact from "./scenes/contact";
import Sidebaar from "./scenes/global/SideBaar";
import Topbar from "./scenes/global/Topbar";
import TenantRegister from "./scenes/register/TenantRegister";
import CorporateLogin from "./scenes/loginn/CorporateLogin";
import IndividualLogin from "./scenes/loginn/IndividualLogin";
import TenDashboard from "./scenes/dashboard/TenDashboard";
import TenantProfile from "./scenes/profile/tenantProfile"
import TenantSubscription from "./scenes/subscription/TenantSubscription"
import CreateNewFile from "./scenes/createFile"
import TenantDoctors from "./scenes/tenant-doctors"
import TenantPatients from "./scenes/tenant-patients"
import TenantsDoctorProfile from "./scenes/profile/tenantsDoctorProfile"
import TenantsPatientProfile from "./scenes/profile/TenantsPatientProfile";
import CreateNewForm from "./scenes/createForm"
import Profile from "./scenes/profile/doctorProfile"
import DoctorDoctorProfile from "./scenes/profile/DoctorDoctorProfile"
import File from "./scenes/file/File"
import PatientProfile from "./scenes/profile/PatientProfile";
import PatientStatistics from "./scenes/profile/PatientStatistics";
import PatientsStatistics from "./scenes/patients/PatientsStatistics";
import FormInfo from "./scenes/formInfo/FormInfo";
import FormUEdit from "./scenes/formEdit/FormEdit.jsx";
import FormFillOut from "./scenes/formFillOut/FormFillOut.jsx";
import Analyze from "./scenes/analyze/Analyze.jsx";
import FilledFormInfo from "./scenes/filledFormInfo/FilledFormInfo.jsx";
import PublicFormPage from "./scenes/publicFormPage/PublicFormPage.jsx";
import DoctorInvitationComplete from "./scenes/doctor-invitation/DoctorInvitationComplete.jsx";
import InviteAdminDoctor from "./scenes/tenant-invite-doctor/InviteAdminDoctor.jsx";
import InviteDoctor from "./scenes/invite-doctor/InviteDoctor.jsx";
import Formulas from "./scenes/formulas";
import CreateFormula from "./scenes/formulas/CreateFormula";
import ViewFormula from "./scenes/formulas/ViewFormula";
import EditFormula from "./scenes/formulas/EditFormula";
// Password Reset Imports
import DoctorForgotPassword from "./scenes/password-reset/DoctorForgotPassword.jsx";
import DoctorVerifyOtp from "./scenes/password-reset/DoctorVerifyOtp.jsx";
import DoctorResetPassword from "./scenes/password-reset/DoctorResetPassword.jsx";
import TenantForgotPassword from "./scenes/password-reset/TenantForgotPassword.jsx";
import TenantVerifyOtp from "./scenes/password-reset/TenantVerifyOtp.jsx";
import TenantResetPassword from "./scenes/password-reset/TenantResetPassword.jsx";
// Super Admin Imports
import SuperAdminLogin from "./scenes/SüperAdminScenes/login/SuperAdminLogin";
import SuperAdminLayout from "./scenes/SüperAdminScenes/global/SuperAdminLayout";
import SuperAdminDashboard from "./scenes/SüperAdminScenes/dashboard/SuperAdminDashboard";
import TenantsPage from "./scenes/SüperAdminScenes/tenants";
import DoctorsPage from "./scenes/SüperAdminScenes/doctors";
import PatientsPage from "./scenes/SüperAdminScenes/patients";
import FormsPage from "./scenes/SüperAdminScenes/forms";
import FormAnswersPage from "./scenes/SüperAdminScenes/form-answers";
import FormInvitationsPage from "./scenes/SüperAdminScenes/form-invitations";
import ScoringFormulasPage from "./scenes/SüperAdminScenes/scoring-formulas";
import FilesPage from "./scenes/SüperAdminScenes/files";
import DoctorInvitationsPage from "./scenes/SüperAdminScenes/doctor-invitations";
import PasswordResetsPage from "./scenes/SüperAdminScenes/password-resets";
import TrashLogsPage from "./scenes/SüperAdminScenes/logs";
import SuperAdminPlans from "./scenes/SüperAdminScenes/plans";
// Permission Groups Imports
import PermissionGroupsList from "./scenes/tenant-permission-groups/PermissionGroupsList";
import CreatePermissionGroup from "./scenes/tenant-permission-groups/CreatePermissionGroup";
import ViewPermissionGroup from "./scenes/tenant-permission-groups/ViewPermissionGroup";
import DoctorPermissionGroupsList from "./scenes/doctor-permission-groups/PermissionGroupsList";
import DoctorCreatePermissionGroup from "./scenes/doctor-permission-groups/CreatePermissionGroup";
import DoctorViewPermissionGroup from "./scenes/doctor-permission-groups/ViewPermissionGroup";
// 404 Not Found Pages
import DoctorNotFound from "./scenes/notFoundPages/DoctorNotFound";
import TenantNotFound from "./scenes/notFoundPages/TenantNotFound";
import PublicNotFound from "./scenes/notFoundPages/PublicNotFound";
import PublicRouteGuard from "./components/PublicRouteGuard";
import TenantRouteGuard from "./components/TenantRouteGuard";
import ErrorBoundary from "./components/ErrorBoundary";

function App() {
  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Sayfa yüklendiğinde nationality'ye göre dil ayarla
  useEffect(() => {
    setLanguageFromStoredNationality();
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PermissionAlertProvider>
        <ErrorBoundary>
        <div className="app">
          <Routes>
            {/* Başlangıçta Marketing sayfasına yönlendirme */}
            <Route 
              path="/" 
              element={
                <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                  <Marketing darkMode={darkMode} setDarkMode={setDarkMode} />
                </Box>
              } 
            />      
            <Route path="/individual-login" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <IndividualLogin darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/corporate-login" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <CorporateLogin darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/about" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <About darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/contact" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <Contact darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/tenantRegister" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <TenantRegister darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/tenant/checkout" element={<Navigate to="/tenant/subscription" replace />} />
            <Route path="/tenant/dashboard" element={
              <TenantRouteGuard>
                <TenDashboard darkMode={darkMode} setDarkMode={setDarkMode} />
              </TenantRouteGuard>
            } />
            <Route path="/tenant/profile" element={
              <TenantRouteGuard>
                <TenantProfile/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/subscription/change" element={<Navigate to="/tenant/subscription" replace />} />
            <Route path="/tenant/subscription" element={
              <TenantRouteGuard>
                <TenantSubscription />
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/doctors" element={
              <TenantRouteGuard>
                <TenantDoctors/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/patients" element={
              <TenantRouteGuard>
                <TenantPatients/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/doctors/:id" element={
              <TenantRouteGuard>
                <TenantsDoctorProfile/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/patients/:id" element={
              <TenantRouteGuard>
                <TenantsPatientProfile/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups" element={
              <TenantRouteGuard>
                <PermissionGroupsList/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups/create" element={
              <TenantRouteGuard>
                <CreatePermissionGroup/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups/edit/:id" element={
              <TenantRouteGuard>
                <CreatePermissionGroup/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups/:id" element={
              <TenantRouteGuard>
                <ViewPermissionGroup/>
              </TenantRouteGuard>
            }/>
            {/* Tenant 404 - Catch all unmatched /tenant/* routes */}
            <Route path="/tenant/*" element={
              <TenantRouteGuard>
                <TenantNotFound />
              </TenantRouteGuard>
            } />
            <Route path="/api/public/forms/:token" element={<PublicFormPage />} />
            
            {/* 🆕 Doctor Invitation Routes */}
            <Route path="/invite/doctor/:token" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <DoctorInvitationComplete />
              </Box>
            } />
            
            {/* Tenant Routes */}
            <Route path="/tenant/invite-admin-doctor" element={
              <TenantRouteGuard>
                <InviteAdminDoctor />
              </TenantRouteGuard>
            } />
            
            {/* Password Reset Routes - Doctor */}
            <Route path="/forgot-password/doctor" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <DoctorForgotPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-otp/doctor" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <DoctorVerifyOtp darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/reset-password/doctor" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <DoctorResetPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            
            {/* Password Reset Routes - Tenant */}
            <Route path="/forgot-password/tenant" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <TenantForgotPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-otp/tenant" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <TenantVerifyOtp darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/reset-password/tenant" element={
              <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <TenantResetPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            
            {/* Super Admin Routes */}
            <Route path="/super-admin/auth/secure-access/login" element={<SuperAdminLogin />} />
            <Route
              path="/super-admin/*"
              element={
                <SuperAdminLayout>
                  <Routes>
                    <Route path="dashboard" element={<SuperAdminDashboard />} />
                    <Route path="plans" element={<SuperAdminPlans />} />
                    <Route path="billing" element={<Navigate to="/super-admin/plans" replace />} />
                    <Route path="tenants" element={<TenantsPage />} />
                    <Route path="doctors" element={<DoctorsPage />} />
                    <Route path="patients" element={<PatientsPage />} />
                    <Route path="forms" element={<FormsPage />} />
                    <Route path="form-answers" element={<FormAnswersPage />} />
                    <Route path="form-invitations" element={<FormInvitationsPage />} />
                    <Route path="scoring-formulas" element={<ScoringFormulasPage />} />
                    <Route path="files" element={<FilesPage />} />
                    <Route path="doctor-invitations" element={<DoctorInvitationsPage />} />
                    <Route path="password-resets" element={<PasswordResetsPage />} />
                    <Route path="trash-logs" element={<TrashLogsPage />} />
                  </Routes>
                </SuperAdminLayout>
              }
            />
            
            <Route
              path="/*"
              element={
                <PublicRouteGuard>
                  <>
                    <Sidebaar 
                      isSidebar={isSidebar} 
                      isCollapsed={isSidebarCollapsed}
                      setIsCollapsed={setIsSidebarCollapsed}
                    />
                    <main 
                      className="content"
                      style={{
                        marginLeft: isSidebarCollapsed ? '80px' : '280px',
                        transition: 'margin-left 0.3s ease',
                      }}
                    >
                      <Topbar setIsSidebar={setIsSidebar} />
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/team" element={<Team />} />
                        <Route path="/team/statistics" element={<TeamStatistics />} />
                        <Route path="/bar" element={<Bar />} />
                        <Route path="/patients" element={<Patients />} />
                        <Route path="/faq" element={<Faq />} />
                        {/* 🆕 Admin Doctor Invite Route */}
                        <Route path="/inviteDoctor" element={<InviteDoctor />} />
                        {/* 🧮 Scoring Formulas Routes */}
                        <Route path="/formulas" element={<Formulas />} />
                        <Route path="/formulas/create" element={<CreateFormula />} />
                        <Route path="/formulas/edit/:id" element={<EditFormula />} />
                        <Route path="/formulas/:id" element={<ViewFormula />} />
                        <Route path="/calendar" element={<Calendar />} />
                        <Route path="/files" element={<Files />} />
                        <Route path="/files/statistics" element={<FilesStatistics />} />
                        <Route path="/line" element={<Li̇ne />} />

                        <Route path="/AddPatient" element={<AddPatient />} />
                        <Route path="/Create-NewFile" element={<CreateNewFile/>} />
                        <Route path="/Create-NewForm/:id" element={<CreateNewForm/>} />
                        <Route path="/profile" element={<Profile/>} />
                        <Route path="/doctors/:id" element={<DoctorDoctorProfile/>} />
                        <Route path="/files/:id" element={<File/>} />
                        <Route path="/patients/statistics" element={<PatientsStatistics />} />
                        <Route path="/patients/:id" element={<PatientProfile/>} />
                        <Route path="/patients/:id/statistics" element={<PatientStatistics/>} />
                        <Route path="/files/:id/:formId" element={<FormInfo/>} />
                        <Route path="/files/:id/:formId/edit" element={<FormUEdit/>}/>
                        <Route path="/files/:id/:formId/fill-out" element={<FormFillOut/>}/>
                        <Route path="/patients/:id/:mrId" element={<Analyze/>} />
                        <Route path="/patients/:id/form/:formId" element={<FilledFormInfo/>} />
                        
                        {/* Doctor Permission Groups Routes */}
                        <Route path="/doctor-permission-groups" element={<DoctorPermissionGroupsList/>}/>
                        <Route path="/doctor-permission-groups/create" element={<DoctorCreatePermissionGroup/>}/>
                        <Route path="/doctor-permission-groups/edit/:id" element={<DoctorCreatePermissionGroup/>}/>
                        <Route path="/doctor-permission-groups/:id" element={<DoctorViewPermissionGroup/>}/>
                        {/* 404 Route */}
                        <Route path="/404" element={<DoctorNotFound />} />
                        {/* Doctor 404 - Catch all unmatched routes */}
                        <Route path="*" element={<DoctorNotFound />} />
                      </Routes>
                    </main>
                  </>
                </PublicRouteGuard>
              }
            />
          </Routes>
        </div>
        </ErrorBoundary>
        </PermissionAlertProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
